import { captureException } from '@sentry/nextjs'
import { getGeocode, getLatLng } from 'use-places-autocomplete'

import tzlookup from '@photostructure/tz-lookup'

export interface GeocodingResultI {
  lat: string
  lon: string
  timezone?: string
  success: boolean
  error?: string
}

/**
 * Geocodes a single address to latitude, longitude, and timezone
 * @param address - The address string to geocode
 * @returns Promise resolving to geocoding result with lat/lon/timezone or error
 */
export const geocodeAddress = async (
  address: string
): Promise<GeocodingResultI> => {
  try {
    if (!address || address.trim() === '') {
      return {
        lat: '',
        lon: '',
        success: false,
        error: 'Empty address',
      }
    }

    const results = await getGeocode({ address })
    if (!results || results.length === 0) {
      return {
        lat: '',
        lon: '',
        success: false,
        error: 'Address not found',
      }
    }

    const { lat, lng } = await getLatLng(results[0])

    // Get timezone from coordinates
    let timezone: string | undefined
    try {
      timezone = tzlookup(lat, lng)
    } catch (error) {
      // Timezone lookup failed, but we still have coordinates
      captureException(error)
      timezone = undefined
    }

    return {
      lat: lat.toString(),
      lon: lng.toString(),
      timezone,
      success: true,
    }
  } catch (error) {
    captureException(error)
    return {
      lat: '',
      lon: '',
      success: false,
      error: 'Geocoding failed',
    }
  }
}

/**
 * Gets timezone from latitude and longitude coordinates
 * @param lat - Latitude as string
 * @param lon - Longitude as string
 * @returns Timezone string or undefined if lookup fails
 */
export const getTimezoneFromCoordinates = (
  lat: string,
  lon: string
): string | undefined => {
  try {
    if (!lat || !lon || lat.trim() === '' || lon.trim() === '') {
      return undefined
    }
    const latNum = parseFloat(lat)
    const lonNum = parseFloat(lon)

    if (isNaN(latNum) || isNaN(lonNum)) {
      return undefined
    }

    return tzlookup(latNum, lonNum)
  } catch (error) {
    captureException(error)
    return undefined
  }
}

/**
 * Geocodes multiple addresses with rate limiting to avoid API quota issues
 * @param addresses - Array of addresses to geocode
 * @param delayMs - Delay between geocoding requests in milliseconds (default: 200ms)
 * @returns Promise resolving to array of geocoding results
 */
export const geocodeAddressesBatch = async (
  addresses: string[],
  delayMs = 200
): Promise<GeocodingResultI[]> => {
  const results: GeocodingResultI[] = []

  for (const address of addresses) {
    const result = await geocodeAddress(address)
    results.push(result)

    // Add delay between requests to avoid hitting API rate limits
    if (addresses.indexOf(address) < addresses.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }

  return results
}
